/* eslint-disable sort-keys-fix/sort-keys-fix */
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { RELEASE_NOTES_URL } from 'utils/constants';
import * as R from 'ramda';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

import { useUserRolePermissions } from 'hooks';

// icons
import SwiftLogo from 'assets/img/dashboard-swift-logo.svg';
import MagellanLogo from 'assets/img/dashboard-magellan-logo.svg';

import LowPriorityIcon from '@mui/icons-material/LowPriority';
import LockIcon from '@mui/icons-material/Lock';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ContactsIcon from '@mui/icons-material/Contacts';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import PercentIcon from '@mui/icons-material/Percent';

import ArtTrackIcon from '@mui/icons-material/ArtTrackRounded';

// Components
import SmallModuleButton from 'common/SmallModuleButton';
import packageJson from '../../../../package.json';

const LoadingManagementDashboard = () => {
  const { currentAffiliate, isInventoryOwner, isProductLoadingView, isSupplier } =
    useUserRolePermissions();

  const supplierOrInventoryOwner = isSupplier || isInventoryOwner;
  const hasInventoryOwnerAccess = isProductLoadingView && isInventoryOwner;
  const hasSupplierAccess = isProductLoadingView && isSupplier;
  const hasBothAccess = isProductLoadingView && supplierOrInventoryOwner;
  const isSelfAuthListViewEnabled = EvaluateFlag(process.env.REACT_APP_LD_SELF_AUTH_LIST_VIEW);

  const buttons = [
    {
      disabled: !hasInventoryOwnerAccess,
      icon: <AdminPanelSettingsRoundedIcon fontSize="inherit" />,
      label: 'Self Authorizations',
      loading: R.isNil(currentAffiliate),
      to: isSelfAuthListViewEnabled
        ? 'self-authorizations/list-view'
        : 'self-authorizations/location',
    },
    {
      disabled: !hasInventoryOwnerAccess,
      icon: <AccountBoxIcon fontSize="inherit" />,
      label: 'Supplier Authorizations',
      loading: R.isNil(currentAffiliate),
      to: 'supplier-authorizations/supplier',
    },
    {
      disabled: !hasSupplierAccess,
      icon: <LowPriorityIcon fontSize="inherit" sx={{ transform: 'rotate(180deg)' }} />,
      label: 'Authorization Sequencing',
      loading: R.isNil(currentAffiliate),
      to: 'authorization-sequencing',
    },
    {
      disabled: !hasSupplierAccess,
      icon: <LockIcon fontSize="inherit" />,
      label: 'My Authorizations',
      loading: R.isNil(currentAffiliate),
      to: 'my-authorizations/inventory-owner/',
    },
    {
      disabled: !hasSupplierAccess,
      icon: <LocalShippingIcon fontSize="inherit" />,
      label: 'Carrier Authorizations',
      loading: R.isNil(currentAffiliate),
      to: 'carrier-authorizations/carrier',
    },
    {
      disabled: !hasSupplierAccess,
      icon: <ContactsIcon fontSize="inherit" />,
      label: 'Customer Authorizations',
      loading: R.isNil(currentAffiliate),
      to: 'customer-authorizations',
    },
    {
      disabled: !hasBothAccess,
      icon: <ArtTrackIcon fontSize="inherit" />,
      label: 'Terminal Information',
      loading: R.isNil(currentAffiliate),
      to: 'terminal-information/terminal-locator/',
    },
    {
      disabled: !hasBothAccess,
      icon: <PercentIcon fontSize="inherit" />,
      label: 'Allocation Loading',
      loading: R.isNil(currentAffiliate),
      to: 'terminal-allocation-loading-percentage',
    },
  ];

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'initial', sm: '100vh' },
        justifyContent: 'space-between',
        pb: 6,
        pt: 7,
        px: 2,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: '1 0',
          justifyContent: 'center',
          mx: 0,
          my: 2,
        }}
      >
        <Link to="../..">
          <img alt="Swift" src={SwiftLogo} />
        </Link>
      </Box>
      <Box
        sx={{
          alignSelf: 'center',
          display: 'grid',
          gridGap: { xs: 'initial', sm: 8 },
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gridTemplateRows: { xs: 'repeat(3, 1fr)', sm: 'repeat(2, 1fr)' },
          width: { xs: 'initial', sm: 556 },
        }}
      >
        {buttons.map(({ label, ...rest }) => (
          <SmallModuleButton key={label} label={label} {...rest} />
        ))}
      </Box>
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flex: '1 0',
          justifyContent: 'center',
          mx: 0,
          my: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Button
            href={RELEASE_NOTES_URL}
            sx={{
              mb: 'auto',
              mt: 2,
              mx: 'auto',
              padding: '2px 0',
              width: 56,
            }}
            target="_blank"
            variant="text"
          >
            Release Notes
          </Button>
          <img
            alt="Magellan Midstream Partners, L.P."
            src={MagellanLogo}
            style={{ marginBottom: '24px' }}
          />
          <Typography align="center" sx={{ mb: 3 }} variant="caption">
            Version {packageJson.version}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingManagementDashboard;
